import { buildAuthorizedMenuTree } from "./menuTree";
import { normaliseRoutePath } from "./routePath";
import { flattenMenuTree, getFirstNavigablePath } from "./treeUtils";

// Keep this module's former public exports as a compatibility facade. The
// route-record builder below stays separate from menu-data construction so it
// can remain a pure "menu tree -> route records" utility.
export {
  buildAuthorizedMenuTree,
  flattenMenuTree,
  getFirstNavigablePath,
};

function hasOwn(object, key) {
  return Object.prototype.hasOwnProperty.call(object, key);
}

function isRecord(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function createRouteMeta(node, hasChildren) {
  const buttonList =
    node && node.meta && Array.isArray(node.meta.btns) && !hasChildren
      ? node.meta.btns.slice()
      : [];

  return {
    title: node && typeof node.name === "string" ? node.name : "",
    btns: buttonList,
  };
}

function getInternalContainerPath(node, prefix) {
  const menuId = node && node.menuId;
  const safeMenuId = encodeURIComponent(
    menuId === undefined || menuId === null ? "unknown" : String(menuId)
  );

  return prefix + "/" + safeMenuId;
}

function getFirstRouteRecordPath(records) {
  const list = Array.isArray(records) ? records : [];

  for (let index = 0; index < list.length; index += 1) {
    const record = list[index];
    const childPath = getFirstRouteRecordPath(record && record.children);

    if (childPath) {
      return childPath;
    }

    if (record && typeof record.path === "string" && record.path) {
      return record.path;
    }
  }

  return null;
}

/**
 * Converts a menu tree to Vue Router route records without importing Vue.
 * Component resolution is supplied by the caller, so this utility remains
 * independently testable and owns all tree recursion in one place.
 *
 * A backend `/Layout` URL is a historical container marker, not a view file.
 * It becomes an internal unique wrapper path. Real leaf paths stay unchanged.
 */
export function createMenuRouteRecords(tree, options) {
  const config = options || {};
  const layoutPath = normaliseRoutePath(config.layoutPath) || "/Layout";
  const containerPathPrefix =
    normaliseRoutePath(config.containerPathPrefix) || "/__menu";
  const resolvePageComponent =
    typeof config.resolvePageComponent === "function"
      ? config.resolvePageComponent
      : function() {
          return null;
        };
  const usedPaths = Object.create(null);

  function reservePath(path) {
    if (!path || hasOwn(usedPaths, path)) {
      return false;
    }

    usedPaths[path] = true;
    return true;
  }

  function createLeafRecord(node) {
    const path = normaliseRoutePath(node && node.path);

    if (!path || path === layoutPath || !reservePath(path)) {
      return null;
    }

    return {
      path: path,
      component: resolvePageComponent(path, node),
      name: node.routeName || "menu-" + String(node.menuId),
      meta: createRouteMeta(node, false),
    };
  }

  function createContainerPath(node) {
    const suppliedPath = normaliseRoutePath(node && node.path);
    const preferredPath =
      suppliedPath && suppliedPath !== layoutPath
        ? suppliedPath
        : getInternalContainerPath(node, containerPathPrefix);

    if (reservePath(preferredPath)) {
      return preferredPath;
    }

    const fallbackPath = getInternalContainerPath(node, containerPathPrefix);
    return reservePath(fallbackPath) ? fallbackPath : "";
  }

  function createNodeRecord(node, depth) {
    if (!isRecord(node)) {
      return null;
    }

    const childRecords = (Array.isArray(node.children) ? node.children : [])
      .map(function(child) {
        return createNodeRecord(child, depth + 1);
      })
      .filter(Boolean);

    if (!childRecords.length) {
      if (!node.isAuthorized) {
        return null;
      }

      const leafRecord = createLeafRecord(node);

      if (!leafRecord || depth > 0) {
        return leafRecord;
      }

      const wrapperPath = createContainerPath(node);
      if (!wrapperPath) {
        return null;
      }

      return {
        path: wrapperPath,
        component: config.layoutComponent,
        name: "menu-container-" + String(node.menuId),
        meta: createRouteMeta(node, true),
        redirect: leafRecord.path,
        children: [leafRecord],
      };
    }

    const containerPath = createContainerPath(node);
    if (!containerPath) {
      return null;
    }

    const firstChildPath = getFirstRouteRecordPath(childRecords);
    const routeRecord = {
      path: containerPath,
      component: depth === 0 ? config.layoutComponent : config.routeViewComponent,
      name: "menu-container-" + String(node.menuId),
      meta: createRouteMeta(node, true),
      children: childRecords,
    };

    if (firstChildPath) {
      routeRecord.redirect = firstChildPath;
    }

    return routeRecord;
  }

  return (Array.isArray(tree) ? tree : [])
    .map(function(node) {
      return createNodeRecord(node, 0);
    })
    .filter(Boolean);
}

export default {
  buildAuthorizedMenuTree: buildAuthorizedMenuTree,
  flattenMenuTree: flattenMenuTree,
  getFirstNavigablePath: getFirstNavigablePath,
  createMenuRouteRecords: createMenuRouteRecords,
};
